import { EmeraldError } from 'src/common/api/emerald-error';
import { TransactionNotFound } from 'src/common/helpers/error-code';
import { CartResponse } from 'src/items-list/business-logic/service/interfaces/cart-response';
import { ICalculatePriceOverride } from '../../common/interfaces/dtos/ICalculatePriceOverride';
import { IOverrideLineItem } from '../../common/interfaces/dtos/IOverrideLineItem';
import { IElectronicJournalRecord } from '../../common/interfaces/states/IElectronicJournalRecord';
import { store } from '../../common/redux/configure-store';
import { applyCartResponse } from '../../home-page/business-logic/common/apply-cart-response';
import { calculatePriceOverride } from './service/calculate-price-override';
import { lineItemPriceModifier } from './service/line-item-price-modifier';
import { addLineItemToTransaction } from '../../home-page/business-logic/sale-mode-router';
import { ILineItemAddData } from '../../items-list/business-logic/line-item-add';
import { IPriceOverridePayload } from '../components/price-override/price-override';
import { PriceModifierAction } from '../../common/enumerations/price-modifier-action';
import {
  LineItemAddDto,
  PriceModifierType,
} from '../../items-list/business-logic/service/interfaces/line-item-add.dto';
import { popupMessage } from '../../messages/business-logic/popup-message';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';

const processPreSalePriceOverride = async (
  value: string,
  action: PriceModifierAction,
  sameSoldChecked: boolean,
  selectedItem?: IElectronicJournalRecord,
  lineItemAddDto?: LineItemAddDto,
): Promise<void> => {
  if (!lineItemAddDto) {
    // something went terribly wrong, but this should never happen...
    popupMessage(new EmeraldError('UnknownErrorCode', 'GeneralErrorIntervention'));
    return;
  }

  const { ItemId, Quantity } = selectedItem || {
    ItemId: '',
    Quantity: 0,
    UnitOfMeasureCode: '',
  };

  const lineItemAdd: ILineItemAddData = {
    inputData: {
      itemId: ItemId,
      quantity: Quantity,
    },
    itemLookup: {},
    lineItem: lineItemAddDto,
  };

  lineItemAdd.lineItem.newPrice = Number(value);
  lineItemAdd.lineItem.modifiers = ['PriceOverride'];
  lineItemAdd.lineItem.priceModifierType = PriceModifierType.PriceOverride;

  await withSpinner(async () => {
    await addLineItemToTransaction(lineItemAdd);

    if (sameSoldChecked) {
      await updateExistingLines(value, action, selectedItem);
    }
  });
};

const updateExistingLines = async (
  value: string,
  action: PriceModifierAction,
  selectedItem?: IElectronicJournalRecord,
): Promise<void> => {
  const calculatePriceOverrideData: ICalculatePriceOverride = {
    TransactionID: store.getState().transaction.transactionId,
    SequenceNumber: selectedItem && selectedItem.SequenceNumber,
    ItemId: selectedItem && selectedItem.ItemId,
    Quantity: selectedItem && selectedItem.Quantity,
    priceModifierValue: Number(value),
    priceModifierAction: action,
  };

  const modifierData: IOverrideLineItem = {
    ...calculatePriceOverrideData,
    PriceModifier: selectedItem?.PriceModifier,
  };

  modifierData.NewPrice = Number(value);
  await lineItemPriceModifier(modifierData, true).then(applyCartResponse);
};

const processPriceOverride = async (
  value: string,
  action: PriceModifierAction,
  sameSoldChecked: boolean,
  selectedItem?: IElectronicJournalRecord,
): Promise<void> => {
  const transactionId = store.getState().transaction.transactionId;

  if (!transactionId) {
    throw new EmeraldError(TransactionNotFound);
  }

  const calculatePriceOverrideData: ICalculatePriceOverride = {
    TransactionID: transactionId,
    SequenceNumber: selectedItem && selectedItem.SequenceNumber,
    ItemId: selectedItem && selectedItem.ItemId,
    Quantity: selectedItem && selectedItem.Quantity,
    priceModifierValue: Number(value),
    priceModifierAction: action,
  };

  const modifierData: IOverrideLineItem = {
    ...calculatePriceOverrideData,
    PriceModifier: selectedItem?.PriceModifier,
  };

  if (selectedItem && !selectedItem.Voided) {
    calculatePriceOverrideData.SequenceNumber = selectedItem.SequenceNumber;
    await withSpinner(() =>
      calculatePriceOverride(calculatePriceOverrideData)
        .then(
          async (data): Promise<CartResponse> => {
            if (data !== undefined) {
              modifierData.NewPrice = data;
            }
            return lineItemPriceModifier(modifierData, sameSoldChecked);
          },
        )
        .then(applyCartResponse),
    );
  }
};

const isPreSale = (lineItem): boolean => {
  return !!lineItem;
};

export const overrideItemPrice = async (
  payload: IPriceOverridePayload,
  selectedItem?: IElectronicJournalRecord,
  lineItem?: LineItemAddDto,
): Promise<void> => {
  const { value, action, sameSoldChecked } = payload;

  isPreSale(lineItem)
    ? await processPreSalePriceOverride(value, action, sameSoldChecked, selectedItem, lineItem)
    : await processPriceOverride(value, action, sameSoldChecked, selectedItem);
};
